import { useMemo } from 'react';
import * as R from 'remeda';
import { ConfigurationMapResponse } from '@rossum/api-client/internal';
import { Schema } from '@rossum/api-client/schemas';
import { extensionDetailPath } from '../../extensions/routes';
import { ruleManagerPath } from '../../rule-manager/routes';
import { getFlattenSchemaFields } from '../../schemas/getFlattenSchemaFields';

type ConfigMapData = ConfigurationMapResponse['results'];

/**
 * Builds URL lookup map for config map nodes.
 *
 * Field URLs are derived from schema content (schema tree paths).
 * Rule URLs are derived from API response data (rule IDs).
 * Hook URLs are derived from API response data (hook IDs).
 *
 * - `nodeUrlMap`: node ID → detail page URL
 */
export const useNodeMaps = (
  data: ConfigMapData | undefined,
  schema: Schema | undefined,
  parentPath: string
) =>
  useMemo(() => {
    const schemaUrls = schema?.content
      ? R.pipe(
          Object.entries(getFlattenSchemaFields(schema.content)),
          R.filter(([, { path }]) => path != null && path.length > 0),
          R.map(([id, { path }]) => {
            const segments = (path ?? []).map(p => p.id).join('/');
            return [id, `${parentPath}/fields/${segments}`] as const;
          }),
          R.fromEntries()
        )
      : {};

    const ruleUrls = R.pipe(
      data?.rules ?? [],
      R.map(r => [r.id, `${ruleManagerPath()}/${r.ruleId}/detail`] as const),
      R.fromEntries()
    );

    const hookUrls = R.pipe(
      data?.hooks ?? [],
      R.map(h => [h.id, extensionDetailPath(h.hookId)] as const),
      R.fromEntries()
    );

    return { nodeUrlMap: { ...schemaUrls, ...ruleUrls, ...hookUrls } };
  }, [data, schema, parentPath]);
